import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TrendChart, { Sparkline } from './TrendChart';
import VectorComparison, { QuickCompare } from './VectorComparison';
import SavedInvestigationsPanel from './SavedInvestigationsPanel';
import CompanyNotesPanel from './CompanyNotesPanel';
import ScheduleControl from './ScheduleControl';
import GlassSelect from './GlassSelect';
import ContactPathLink from './ContactPathLink';
import WideTableScroll from './WideTableScroll';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PipelineDashboard({ apiKey, onSelectCompany }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('risk_score');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterRecommendation, setFilterRecommendation] = useState('');
  const [filterMinRisk, setFilterMinRisk] = useState('');
  const [filterMaxRisk, setFilterMaxRisk] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [sparklineData, setSparklineData] = useState({});
  const [compareCompany, setCompareCompany] = useState(null);
  // P0 demo-safety: surface a calm error state instead of crashing when
  // the pipeline endpoint returns an error payload (e.g. JWT-only sessions
  // where no API key is set and the server replies with `{detail: "..."}`).
  const [fetchError, setFetchError] = useState('');

  // Only attach `X-API-Key` when we actually have one — sending the
  // literal string "undefined" causes the backend to 401 the request,
  // which used to land in `data` and crash `data.items.map(...)` below.
  // JWT-cookie auth still works without the header.
  const headers = (() => {
    const h = { 'Content-Type': 'application/json' };
    if (apiKey && typeof apiKey === 'string' && apiKey.trim()) {
      h['X-API-Key'] = apiKey;
    }
    return h;
  })();

  const fetchPipeline = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort_by: sortBy, sort_order: sortOrder, limit: '50' });
      if (filterIndustry) params.set('industry', filterIndustry);
      if (filterRecommendation) params.set('recommendation', filterRecommendation);
      if (filterMinRisk) params.set('min_risk', filterMinRisk);
      if (filterMaxRisk) params.set('max_risk', filterMaxRisk);

      const resp = await fetch(`${API_URL}/api/pipeline?${params}`, { headers, credentials: 'include' });
      const result = await resp.json().catch(() => ({}));
      // Reject error / unexpected payloads BEFORE they reach the
      // `data.items.map(...)` consumers further down the page.
      if (!resp.ok || !Array.isArray(result?.items)) {
        const detail = typeof result?.detail === 'string'
          ? result.detail
          : 'Pipeline could not be loaded. Please try again.';
        setFetchError(detail);
        setData({ items: [], total: 0 });
        return;
      }
      setFetchError('');
      setData(result);

      // Fetch sparkline data for companies that have analyses
      const items = Array.isArray(result.items) ? result.items : [];
      const withAnalysis = items.filter(i => i.analysis_count > 1);
      if (withAnalysis.length > 0) {
        const sparkPromises = withAnalysis.slice(0, 20).map(async (item) => {
          try {
            const r = await fetch(`${API_URL}/api/analysis/history/${item.id}?limit=20`, { headers, credentials: 'include' });
            if (r.ok) {
              const h = await r.json();
              return [item.id, h.history || []];
            }
          } catch {}
          return [item.id, []];
        });
        const results = await Promise.all(sparkPromises);
        const sparkMap = {};
        results.forEach(([id, hist]) => { sparkMap[id] = hist; });
        setSparklineData(sparkMap);
      }
    } catch (e) {
      console.error('Pipeline fetch failed:', e);
      setFetchError('Pipeline could not be loaded. Please try again.');
      setData({ items: [], total: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPipeline(); }, [sortBy, sortOrder, filterIndustry, filterRecommendation, filterMinRisk, filterMaxRisk]);

  // Search-driven landing: when GlobalSearch routes here with ?company=<id>,
  // wait for the data to mount, scroll the row into view, apply a 1s amber
  // highlight ring, and clear the URL param. Fail silently if the company is
  // not in the current filtered set — never raises.
  useEffect(() => {
    if (!data) return undefined;
    const params = new URLSearchParams(location.search);
    const companyId = params.get('company');
    if (!companyId) return undefined;
    const t = setTimeout(() => {
      try {
        const row = document.querySelector(`[data-testid="pipeline-row-${companyId}"]`);
        if (row) {
          row.scrollIntoView({ behavior: 'smooth', block: 'center' });
          row.classList.add('qf-row-highlight');
          // Auto-remove after the keyframe completes so it doesn't linger
          setTimeout(() => row.classList.remove('qf-row-highlight'), 1400);
        }
        // else: company isn't in the current filtered set — silent fail.
      } catch { /* never throw on a presentational hint */ }
      // Always strip ?company= so refresh / back-nav doesn't re-trigger
      const params2 = new URLSearchParams(location.search);
      params2.delete('company');
      const q = params2.toString();
      navigate(q ? `${location.pathname}?${q}` : location.pathname, { replace: true });
    }, 60);
    return () => clearTimeout(t);
  }, [data, location.search, location.pathname, navigate]);

  // Auto-expand a row when navigated to via /?view=pipeline&company=<id>
  const routerLocation = useLocation();
  const routerNavigate = useNavigate();
  useEffect(() => {
    if (!data || !data.items) return;
    const params = new URLSearchParams(routerLocation.search);
    const target = params.get('company');
    if (target && data.items.some((i) => i.id === target)) {
      setExpandedRow(target);
      // Strip the param so reload/refresh doesn't keep re-forcing the row
      params.delete('company');
      const q = params.toString();
      routerNavigate({ pathname: '/', search: q ? `?${q}` : '' }, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const industries = useMemo(() => {
    const items = Array.isArray(data?.items) ? data.items : [];
    if (items.length === 0) return [];
    const set = new Set(items.map(i => i.industry).filter(Boolean));
    return Array.from(set).sort();
  }, [data]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getRiskBadge = (score) => {
    if (score === null || score === undefined) return <span className="badge badge-type">N/A</span>;
    const cls = score >= 70 ? 'badge-high' : score >= 40 ? 'badge-medium' : 'badge-low';
    return <span className={`badge ${cls}`} style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px' }}>{score}</span>;
  };

  const getRecBadge = (rec) => {
    if (!rec) return <span className="badge badge-type">No analysis</span>;
    const label = rec.replace(/_/g, ' ');
    const cls = rec.includes('high') ? 'badge-high' : rec.includes('medium') ? 'badge-medium' : rec.includes('low') ? 'badge-low' : 'badge-type';
    return <span className={`badge ${cls}`}>{label}</span>;
  };

  const getVerificationIndicator = (item) => {
    const vs = item.verification_status;
    const ep = item.evidence_profile || {};
    if (!vs && !ep.verification_status) {
      return <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>-</span>;
    }
    const status = vs || ep.verification_status || '';
    const sourceCount = ep.source_count || 0;
    const diversityPct = Math.round((ep.source_diversity_score || 0) * 100);
    const corrobPct = Math.round((ep.corroboration_score || 0) * 100);

    const statusColor = {
      verified: 'var(--success)',
      corroborated: 'var(--primary)',
      partial: 'var(--warning)',
      low_evidence: 'var(--danger)',
      single_source: 'var(--danger)',
      needs_review: 'var(--text-secondary)',
    }[status] || 'var(--text-secondary)';

    const strengthPct = status === 'verified' ? 100 :
      status === 'corroborated' ? 70 :
      status === 'partial' ? 45 :
      status === 'low_evidence' ? 20 :
      status === 'single_source' ? 15 : 10;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '72px' }}
        data-testid={`evidence-gauge-${item.id}`}
        title={`${status.replace(/_/g, ' ')} | ${sourceCount} sources | Diversity ${diversityPct}% | Corroboration ${corrobPct}%`}
      >
        {/* Mini bar gauge */}
        <div style={{ height: '4px', borderRadius: '2px', background: 'var(--border)', width: '100%', overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: '2px', background: statusColor,
            width: `${strengthPct}%`, transition: 'width 0.3s'
          }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
          <span style={{
            display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%',
            background: statusColor, flexShrink: 0
          }} />
          <span style={{ fontSize: '9px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap' }}>
            {status === 'single_source' ? '1 src' :
             status === 'low_evidence' ? 'low' :
             status === 'needs_review' ? 'n/a' :
             status === 'corroborated' ? 'corrob' :
             status === 'verified' ? 'verified' :
             status === 'partial' ? 'partial' : '-'}
          </span>
          {sourceCount > 0 && (
            <span style={{ fontSize: '9px', fontFamily: "'IBM Plex Mono', monospace", color: statusColor }}>
              {sourceCount}s
            </span>
          )}
        </div>
      </div>
    );
  };

  const sortIndicator = (field) => {
    if (sortBy !== field) return '';
    return sortOrder === 'desc' ? ' \u25BC' : ' \u25B2';
  };

  const toggleExpand = (id) => {
    setExpandedRow(prev => prev === id ? null : id);
  };

  return (
    <div>
      {/* Calm error surface — replaces the prior runtime crash when the
          Pipeline endpoint returns an error payload (e.g. JWT-only
          sessions where no API key is available). Demo-safety guard. */}
      {fetchError && (
        <div
          className="panel"
          style={{ marginBottom: '8px', borderLeft: '3px solid var(--danger)' }}
          data-testid="pipeline-fetch-error"
        >
          <div className="panel-body" style={{ padding: '8px 12px', fontSize: 12, color: 'var(--danger)' }}>
            {fetchError}
          </div>
        </div>
      )}
      {/* Filters */}
      <div className="panel" style={{ marginBottom: '8px' }}>
        <div className="panel-header">
          Pipeline Filters
          <span className="badge badge-type" data-testid="pipeline-total">
            {data ? `${data.total ?? (Array.isArray(data.items) ? data.items.length : 0)} companies` : 'Loading...'}
          </span>
        </div>
        <div className="panel-body" style={{ padding: '10px 16px' }}>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ minWidth: '140px' }}>
              <label>Industry</label>
              <GlassSelect
                className="signal-type-select"
                style={{ padding: '6px 8px', width: '100%' }}
                value={filterIndustry}
                onChange={(e) => setFilterIndustry(e.target.value)}
                data-testid="filter-industry"
              >
                <option value="">All Industries</option>
                {industries.map(i => <option key={i} value={i}>{i}</option>)}
              </GlassSelect>
            </div>
            <div className="form-group" style={{ minWidth: '160px' }}>
              <label>Recommendation</label>
              <GlassSelect
                className="signal-type-select"
                style={{ padding: '6px 8px', width: '100%' }}
                value={filterRecommendation}
                onChange={(e) => setFilterRecommendation(e.target.value)}
                data-testid="filter-recommendation"
              >
                <option value="">All</option>
                <option value="high_priority_candidate">High Priority</option>
                <option value="medium_priority_candidate">Medium Priority</option>
                <option value="low_priority_candidate">Low Priority</option>
                <option value="monitor">Monitor</option>
                <option value="not_suitable">Not Suitable</option>
              </GlassSelect>
            </div>
            <div className="form-group" style={{ minWidth: '80px' }}>
              <label>Min Risk</label>
              <input type="number" min="0" max="100" placeholder="0" value={filterMinRisk}
                onChange={(e) => setFilterMinRisk(e.target.value)} style={{ width: '100%', padding: '5px 8px' }}
                data-testid="filter-min-risk" />
            </div>
            <div className="form-group" style={{ minWidth: '80px' }}>
              <label>Max Risk</label>
              <input type="number" min="0" max="100" placeholder="100" value={filterMaxRisk}
                onChange={(e) => setFilterMaxRisk(e.target.value)} style={{ width: '100%', padding: '5px 8px' }}
                data-testid="filter-max-risk" />
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => {
              setFilterIndustry(''); setFilterRecommendation(''); setFilterMinRisk(''); setFilterMaxRisk('');
            }} data-testid="clear-filters-button">Clear</button>
          </div>
        </div>
      </div>

      {/* Table — Iter 38b: outer `overflow:auto` removed so WideTableScroll
          can detect the wrap's natural height below the viewport fold and
          render the floating horizontal scrollbar. */}
      <div className="panel">
        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading pipeline...</div>
        ) : (
          <WideTableScroll testId="pipeline-table-scroll">
          <table className="data-table" data-testid="pipeline-table">
            <thead>
              <tr>
                <th style={{ width: '28px' }}></th>
                <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                  Company{sortIndicator('name')}
                </th>
                <th>Industry</th>
                <th onClick={() => handleSort('risk_score')} style={{ cursor: 'pointer' }}>
                  Risk{sortIndicator('risk_score')}
                </th>
                <th>Evidence</th>
                <th>Trend</th>
                <th>Recommendation</th>
                <th onClick={() => handleSort('signal_count')} style={{ cursor: 'pointer' }}>
                  Signals{sortIndicator('signal_count')}
                </th>
                <th>Patterns</th>
                <th onClick={() => handleSort('last_scan')} style={{ cursor: 'pointer' }}>
                  Last Scan{sortIndicator('last_scan')}
                </th>
                <th>Schedule</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(data?.items) ? data.items : []).map((item) => {
                const isExpanded = expandedRow === item.id;
                const spark = sparklineData[item.id] || [];
                return (
                  <React.Fragment key={item.id}>
                    <tr data-testid={`pipeline-row-${item.id}`}>
                      <td style={{ textAlign: 'center', cursor: 'pointer', fontSize: '12px', color: 'var(--text-secondary)' }}
                        onClick={() => toggleExpand(item.id)}
                        data-testid={`pipeline-expand-${item.id}`}
                      >
                        {isExpanded ? '\u25BC' : '\u25B6'}
                      </td>
                      <td>
                        <span
                          style={{ fontWeight: '600', cursor: 'pointer', color: 'var(--primary)' }}
                          onClick={() => onSelectCompany && onSelectCompany(item)}
                          data-testid={`pipeline-company-${item.id}`}
                        >
                          {item.name}
                        </span>
                        {/* Iter 33c — Contact path / Evidence source
                            inline link. Falls back to the company's
                            stored `website` when neither URL field is
                            populated, so existing companies retain a
                            visible link without re-running Tracker. */}
                        <ContactPathLink
                          targetUrl={item.target_url || item.website}
                          bestEvidenceUrl={item.best_evidence_url}
                          reason={item.target_url_reason}
                          testIdPrefix={`pipeline-contact-path-${item.id}`}
                        />
                        {item.dissolved_state?.is_dissolved && (() => {
                          // Iter 37 / 37-fix — chip label branches on
                          // entity_viability so a UK-only blocker
                          // (Primus-class) reads differently from a
                          // global brand warning (Coinbase-class).
                          const ds = item.dissolved_state || {};
                          const v = ds.entity_viability || '';
                          let label;
                          if (v === 'dissolved_uk_entity_blocker') {
                            label = 'UK legal entity dissolved';
                          } else if (v === 'global_brand_uk_entity_warning') {
                            label = 'UK entity warning';
                          } else if (v === 'match_uncertain_verify') {
                            label = 'UK entity match uncertain';
                          } else {
                            // Legacy fallback for pre-37-fix payloads.
                            const mc = (ds.name_match_confidence || '').toLowerCase();
                            const isGlobalBrand = !!ds.global_brand_match;
                            label = (mc === 'near' || mc === 'loose' || isGlobalBrand)
                              ? 'UK entity match uncertain'
                              : `UK entity ${ds.status_label || 'inactive'}`;
                          }
                          const tooltip =
                            'Companies House status applies to the matched UK ' +
                            'legal entity only. For UK-only entities, a dissolved ' +
                            'status may mean this legal entity should not be ' +
                            'pursued unless a successor or related trading entity ' +
                            'exists. For international brands, verify the current ' +
                            'UK operating entity.';
                          return (
                            <span
                              data-testid={`pipeline-dissolved-chip-${item.id}`}
                              title={tooltip}
                              style={{
                                marginLeft: '6px',
                                fontSize: '9px',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                padding: '2px 6px',
                                borderRadius: '999px',
                                background: 'rgba(120, 113, 108, 0.16)',
                                color: 'var(--text-secondary)',
                                border: '1px solid rgba(120, 113, 108, 0.3)',
                                verticalAlign: 'middle',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {label}
                            </span>
                          );
                        })()}
                        {item.location && <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>{item.location}</div>}
                      </td>
                      <td><span className="badge badge-type">{item.industry || '-'}</span></td>
                      <td>{getRiskBadge(item.overall_risk_score)}</td>
                      <td>{getVerificationIndicator(item)}</td>
                      <td>
                        <Sparkline data={spark} dataKey="overall_risk_score" color="#DC2626" />
                      </td>
                      <td>{getRecBadge(item.recommendation)}</td>
                      <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px' }}>{item.signal_count}</td>
                      <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px' }}>{item.pattern_count}</td>
                      <td style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                        {item.last_scan_date ? new Date(item.last_scan_date).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <ScheduleControl companyId={item.id} companyName={item.name} />
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={11} style={{ padding: '8px 16px', background: 'var(--bg)' }}>
                          <TrendChart companyId={item.id} companyName={item.name} />
                          <div style={{ marginTop: '8px', borderTop: '1px solid var(--border)', paddingTop: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                              <span style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)' }}>Vector Comparison</span>
                              <button className="btn btn-outline btn-sm" style={{ fontSize: '10px', padding: '2px 8px' }}
                                onClick={() => setCompareCompany({ id: item.id, name: item.name })}
                                data-testid={`compare-full-${item.id}`}
                              >
                                Full Comparison View
                              </button>
                              <button className="btn btn-primary btn-sm" style={{ fontSize: '10px', padding: '2px 8px', marginLeft: '6px' }}
                                onClick={() => onSelectCompany && onSelectCompany(item)}
                                data-testid={`scan-now-${item.id}`}
                              >
                                Scan Now
                              </button>
                            </div>
                            <QuickCompare companyId={item.id} companyName={item.name} />
                          </div>
                          <SavedInvestigationsPanel
                            companyId={item.id}
                            onOpen={(invId) => {
                              window.open(`/investigation/${invId}`, '_blank', 'noopener');
                            }}
                          />
                          <CompanyNotesPanel companyId={item.id} apiKey={apiKey} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {data && data.items.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ padding: '32px 20px' }}>
                    {(filterIndustry || filterRecommendation || filterMinRisk || filterMaxRisk) ? (
                      <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}
                           data-testid="pipeline-empty-filtered">
                        <div style={{ fontWeight: 600, color: 'var(--text)' }}>No companies match these filters</div>
                        <div style={{ fontSize: 12, marginTop: 4 }}>
                          Try widening the risk range or clearing the industry / recommendation filters.
                        </div>
                        <button
                          className="btn btn-outline btn-sm"
                          style={{ marginTop: 12 }}
                          onClick={() => {
                            setFilterIndustry(''); setFilterRecommendation('');
                            setFilterMinRisk(''); setFilterMaxRisk('');
                          }}
                          data-testid="pipeline-empty-clear-filters">
                          Clear filters
                        </button>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', maxWidth: 480, margin: '0 auto' }}
                           data-testid="pipeline-empty-first-run">
                        <div style={{ fontSize: 32, marginBottom: 8 }}>🧭</div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                          Your pipeline is empty
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.5 }}>
                          Run Scout on your first company, review the signals, and run an Analysis.
                          Companies you scout and analyse will show up here with risk scores, trends and saved investigations.
                        </div>
                        <button
                          className="btn btn-primary btn-sm"
                          style={{ marginTop: 14 }}
                          onClick={() => window.dispatchEvent(new CustomEvent('quod:go-home'))}
                          data-testid="pipeline-empty-start-scout">
                          Start your first scout →
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </WideTableScroll>
        )}
      </div>

      {/* Full Comparison Modal */}
      {compareCompany && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.7)', display: 'flex',
            justifyContent: 'center', alignItems: 'flex-start',
            padding: '24px', overflow: 'auto',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setCompareCompany(null); }}
          data-testid="comparison-modal"
        >
          <div style={{ background: 'var(--surface)', borderRadius: '8px', padding: '16px', maxWidth: '1100px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
            <VectorComparison
              companyId={compareCompany.id}
              companyName={compareCompany.name}
              onClose={() => setCompareCompany(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
